import type { FocusEvent, ReactNode } from 'react';
import { useContext } from 'react';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';
import { formatMasked } from '@/lib/pages/forms/helpers';

const getRangeLabel = (
  min?: string | number,
  max?: string | number
): string => {
  if (min != null && max != null) return `Range: ${min} to ${max}`;
  if (min != null) return `Range: ${min} to Unlimited`;
  if (max != null) return `Maximum: ${max}`;
  return '';
};

function CustomInput<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = T,
>({
  control,
  name,
  label,
  placeholder,
  min,
  max,
  required = false,
  nativeRequired = false,
  orientation = 'horizontal',
  type = 'text',
  step = 1,
  displayRange,
  disabled,
  className,
  labelClassName,
  wideLabel,
  stretch = false,
  readOnly = false,
  overwriteLabelWidth,
  overwriteInputWidth,
  pattern,
  trim = false,
  disableNumericFormat = false,
  rules,
  onBlur,
}: {
  control: Control<T, unknown, TTransformedValues>;
  // options: string[];
  name: Path<T> | string;
  label: ReactNode;
  placeholder?: string;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  nativeRequired?: boolean;
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'tel'
    | 'password'
    | 'currency'
    | 'percentage'
    | 'zip';
  min?: string | number;
  max?: string | number;
  step?: string | number;
  displayRange?: boolean;
  // disabled might be used to ignore all browser validation constraints
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  wideLabel?: boolean;
  stretch?: boolean;
  readOnly?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  pattern?: string;
  trim?: boolean;
  disableNumericFormat?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  /** Called on blur before the internal trim + field.onBlur() logic. */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}) {
  const { responsive, editMode } = useContext(AgentAuthorityContext);

  const getMask = (type: string) => {
    if (type === 'currency') {
      return 'currency';
    }

    if (type === 'percentage') {
      return 'percentage';
    }

    if (type === 'number' && !disableNumericFormat) {
      return 'number';
    }

    return undefined;
  };

  const getType = (type: string) => {
    if (type === 'currency') {
      return 'number';
    }

    if (type === 'percentage') {
      return 'number';
    }

    if (type === 'number') {
      return 'number';
    }

    if (type === 'zip') {
      return 'number';
    }

    return type;
  };

  const mask = getMask(type);
  // Percentages are left out: nothing there needs a thousands separator.
  const overlaid = mask === 'currency' || mask === 'number';

  return (
    <FormField
      control={control}
      name={name as Path<T>}
      rules={rules}
      render={({ field }) => {
        if (!editMode || readOnly) {
          return (
            <ReadOnlyField
              label={label}
              value={field.value}
              mask={getMask(type)}
              orientation={orientation}
              wideLabel={wideLabel}
              stretch={stretch}
              responsive={responsive}
              overwriteLabelWidth={overwriteLabelWidth}
              overwriteInputWidth={overwriteInputWidth}
            />
          );
        } else {
          // Empty stays empty: with no overlay text the input keeps its own
          // colour, so the placeholder shows through as it always did.
          const overlay = overlaid ? formatMasked(field.value, mask) : '';

          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
            >
              {label ? (
                <FormLabel
                  className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} font-semibold leading-[1.5rem] ${responsive ? 'max-md:w-full' : ''} ${labelClassName}`}
                >
                  {label}
                  {(required || nativeRequired) && !disabled ? (
                    <span className="text-red-400">*</span>
                  ) : (
                    ''
                  )}
                </FormLabel>
              ) : null}

              <div
                className={`relative ${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
              >
                <FormControl>
                  <Input
                    // defaultValue={field.value}
                    type={getType(type)}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step || 1}
                    disabled={disabled}
                    // required={nativeRequired || required} // uncomment to enable FE validation
                    required={nativeRequired}
                    {...field}
                    className={`${className} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${overlay ? 'peer text-transparent focus:text-inherit' : ''}`}
                    pattern={pattern}
                    onBlur={(e) => {
                      onBlur?.(e);
                      // Trim whitespace and remove tabs/newlines if trim is enabled
                      if (trim && typeof e.target.value === 'string') {
                        // Remove tabs and newlines from anywhere in the string, then trim whitespace
                        const cleanedValue = e.target.value
                          .replace(/[\t\n\r]/g, '') // Remove tabs, newlines, and carriage returns
                          .replace(/\s+/g, ' ') // Replace multiple consecutive spaces with a single space
                          .trim(); // Remove leading/trailing whitespace

                        if (cleanedValue !== e.target.value) {
                          field.onChange(cleanedValue);
                        }
                      }
                      field.onBlur();
                    }}
                  />
                </FormControl>
                {/*
                  The formatted value is painted over the input and hidden while
                  it has focus, so the input stays a native `type="number"` —
                  `min`, `max`, `step`, the arrow keys and the browser's own
                  validation all keep working, and the form state is untouched.
                */}
                {overlay ? (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 flex h-10 items-center px-3 text-sm peer-focus:hidden"
                  >
                    {overlay}
                  </span>
                ) : null}
                {displayRange ? (
                  <p className="pl-1 pt-1 text-xs text-gray-400">
                    {getRangeLabel(min, max)}
                  </p>
                ) : null}
                <FormMessage />
              </div>
              {/* <FormDescription>
                This is your public display name.
              </FormDescription> */}
            </FormItem>
          );
        }
      }}
    />
  );
}

export { CustomInput as Input };
